import { AfterViewInit, Component, DoCheck, ElementRef, Input, Renderer2, ViewChild } from '@angular/core';
import { NgbDate, NgbDatepickerI18n, NgbDateStruct, NgbInputDatepicker, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { AbstractFormComponent } from '../_abstract-classes/abstract-form-component';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { BaseCustomDatepickerI18n } from '../datepicker/base-datepicker.i18n';
import { BaseDateFormat, BaseDateService, BASE_DATE_SERVICE_FORMAT } from '../../services/base-date.service';
import { baseRangeDatepickerValidator } from '../../validators/base-range-datepicker.validator';
import { baseDatepickerValidator } from '../../validators/base-datepicker.validator';
import { DEFAULT_MIN_DATE, DEFAULT_MAX_DATE, toNgbDateStruct } from '../../constants/base-datepicker.const';

export interface DateRange {
  from: string;
  to: string;
}

@Component({
  selector: 'base-range-datepicker',
  providers: [
    { provide: NgbDatepickerI18n, useClass: BaseCustomDatepickerI18n },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: BaseRangeDatepickerComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useValue: baseRangeDatepickerValidator,
      multi: true,
    },
  ],
  templateUrl: './range-datepicker.component.html',
})
export class BaseRangeDatepickerComponent
  extends AbstractFormComponent
  implements ControlValueAccessor, AfterViewInit, DoCheck
{
  @ViewChild('d') datePicker: NgbInputDatepicker;
  @ViewChild('fromInput') fromInput: ElementRef;
  @ViewChild('toInput') toInput: ElementRef;
  @ViewChild('calendarInput') calendarInput: ElementRef<HTMLInputElement>;
  @Input() label: string;
  @Input() isRequired: boolean;
  @Input() errorText: string | null;
  @Input() placeholder = 'dd.mm.yyyy';
  @Input() placement = 'auto';
  @Input() isInvalid: boolean;
  @Input() tabIndex: number;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() isFilter = false;
  @Input() minDate: NgbDateStruct = DEFAULT_MIN_DATE;
  @Input() maxDate: NgbDateStruct = DEFAULT_MAX_DATE;
  @Input() disabledDatesSet: Set<string> = new Set<string>();
  @Input() hideSelectedRange = false;
  @Input() allowDisableDateOverlap = false;

  model: DateRange;
  defaultCurrentDate = toNgbDateStruct();

  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  hoveredDate: NgbDate | null;
  currentValueTo: string = '';
  currentValueFrom: string = '';
  overlappedDisabledDates: NgbDate[] = [];
  validationMessageVisible = false;

  constructor(protected renderer: Renderer2, private calendar: NgbCalendar) {
    super(renderer);
  }

  isDisabled = (date: NgbDate) => {
    return this.disabledDatesSet.has(this.ngbDateToString(date));
  };

  ngDoCheck() {
    this.setNgValidationClasses();
    this.clearInput();
  }

  ngAfterViewInit() {
    this.setNgValidationClasses();
  }

  onChange = (range: DateRange) => {
    this.model = range;
  };

  onTouched = () => {};

  writeValue(value: DateRange): void {
    this.model = value || { to: '', from: '' };
    this.fromDate = value?.from ? NgbDate.from(this.fromModel(value.from)) : null;
    this.toDate = value?.to ? NgbDate.from(this.fromModel(value.to)) : null;
  }

  registerOnChange(fn: (date: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateSelect(value: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = NgbDate.from(value);
    } else if (
      this.fromDate &&
      !this.toDate &&
      value &&
      this.fromDate &&
      this.fromDate.year === value.year &&
      this.fromDate.month === value.month &&
      this.fromDate.day === value.day
    ) {
      this.toDate = this.fromDate;
    } else if (this.fromDate && !this.toDate && NgbDate.from(value)?.after(this.fromDate)) {
      if (!this.allowDisableDateOverlap) {
        const overlappedDisabledDates = this.getOverlappedDisabledDates(this.fromDate, value);
        if (overlappedDisabledDates.length > 0) {
          this.overlappedDisabledDates = overlappedDisabledDates;
          this.validationMessageVisible = true;
          this.fromDate = null;
          this.toDate = null;
          this.hoveredDate = null;

          return;
        }
      }

      this.toDate = NgbDate.from(value);
      this.validationMessageVisible = false;
      this.overlappedDisabledDates = [];
    } else {
      this.toDate = null;
      this.fromDate = NgbDate.from(value);
    }
    if (this.fromDate && this.toDate) {
      const range = {
        from: this.toModel(this.fromDate) || '',
        to: this.toModel(this.toDate) || '',
      };
      this.onChange(range);
      this.model = range;
      this.datePicker.toggle();
    }
  }

  onFromChange(event: any): void {
    this.currentValueFrom = event.target.value;
    const invalid = !!baseDatepickerValidator(new UntypedFormControl(event.target.value));
    const formatted = invalid ? '' : BaseDateService.toServerFormatString(event.target.value);
    this.fromDate = formatted ? NgbDate.from(this.fromModel(formatted)) : null;
    if ((this.minDate && this.fromDate?.before(this.minDate)) || (this.maxDate && this.fromDate?.after(this.maxDate))) {
      this.fromDate = null;
      return;
    }
    const value = { from: formatted, to: this.model.to };
    this.model = {
      from: formatted,
      to: this.model.to,
    };
    this.onChange(value);
  }

  onToChange(event: any): void {
    this.currentValueTo = event.target.value;
    const invalid = !!baseDatepickerValidator(new UntypedFormControl(event.target.value));
    const formatted = invalid ? '' : BaseDateService.toServerFormatString(event.target.value);
    this.toDate = formatted ? NgbDate.from(this.fromModel(formatted)) : null;
    if ((this.minDate && this.toDate?.before(this.minDate)) || (this.maxDate && this.toDate?.after(this.maxDate))) {
      this.toDate = null;
      return;
    }
    const value = { from: this.model.from, to: formatted };
    this.model = {
      from: this.model.from,
      to: formatted,
    };
    this.onChange(value);
  }

  isHovered(date: NgbDate) {
    const isHovered =
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);

    if (!isHovered) {
      return false;
    }

    return this.allowDisableDateOverlap ? !this.isDisabled(date) : true;
  }

  isInside(date: NgbDate) {
    const isInsideRange = this.toDate && date.after(this.fromDate) && date.before(this.toDate);

    if (!isInsideRange) {
      return false;
    }

    return this.allowDisableDateOverlap ? !this.isDisabled(date) : true;
  }

  isRange(date: NgbDate) {
    const isDateInRange =
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date);

    if (!isDateInRange) {
      return false;
    }

    return this.allowDisableDateOverlap ? !this.isDisabled(date) : true;
  }

  format(date: NgbDate | null, isFromInput: boolean, isToInput: boolean): string {
    if (this.hideSelectedRange) {
      return '';
    }

    return date
      ? BaseDateService.format(new Date(date.year, date.month - 1, date.day), BaseDateFormat.DateClientFormat)
      : isFromInput
      ? this.currentValueFrom
      : isToInput
      ? this.currentValueTo
      : '';
  }

  onClose() {
    if (this.fromDate && this.toDate === null) {
      this.toDate = NgbDate.from(this.fromDate);
      this.onChange({
        from: this.toModel(this.fromDate) || '',
        to: this.toModel(this.toDate) || '',
      });
    }

    if (!this.fromDate && !this.toDate && this.model?.from && this.model?.to) {
      this.fromDate = NgbDate.from(this.fromModel(this.model.from));
      this.toDate = NgbDate.from(this.fromModel(this.model.to));
    }
  }

  private toModel(date: NgbDateStruct | null): string | null {
    return date
      ? BaseDateService.format(new Date(date.year, date.month - 1, date.day), BaseDateFormat.DateServiceFormat)
      : null;
  }

  private fromModel(value: string | undefined): NgbDateStruct | null {
    if (value) {
      const date = value.split('-');
      return {
        year: parseInt(date[0], 10),
        month: parseInt(date[1], 10),
        day: parseInt(date[2], 10),
      };
    }
    return null;
  }

  private setNgValidationClasses(): void {
    if (!this.fromInput || !this.toInput) {
      return;
    }
    if (this.isInvalid) {
      this.renderer.addClass(this.fromInput.nativeElement, 'ng-invalid');
      this.renderer.removeClass(this.fromInput.nativeElement, 'ng-valid');
      this.renderer.addClass(this.toInput.nativeElement, 'ng-invalid');
      this.renderer.removeClass(this.toInput.nativeElement, 'ng-valid');
    } else {
      this.renderer.addClass(this.fromInput.nativeElement, 'ng-valid');
      this.renderer.removeClass(this.fromInput.nativeElement, 'ng-invalid');
      this.renderer.addClass(this.toInput.nativeElement, 'ng-valid');
      this.renderer.removeClass(this.toInput.nativeElement, 'ng-invalid');
    }
  }

  private clearInput() {
    if (this.hideSelectedRange && this.calendarInput) {
      if (this.calendarInput.nativeElement.value !== '') {
        this.renderer.setProperty(this.calendarInput.nativeElement, 'value', '');
      }
    }
  }

  getOverlappedDisabledDates(start: NgbDate, end: NgbDate): NgbDate[] {
    const overlapped: NgbDate[] = [];
    for (const dateStr of this.disabledDatesSet) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new NgbDate(year, month, day);
      if (!date.before(start) && !date.after(end)) {
        overlapped.push(date);
      }
    }
    return overlapped;
  }

  ngbDateToString(ngbDate: NgbDate): string {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    return BaseDateService.format(date, BASE_DATE_SERVICE_FORMAT);
  }

  includesDisabledDate(start: NgbDate, end: NgbDate): boolean {
    if (!this.disabledDatesSet || this.disabledDatesSet.size === 0) {
      return false;
    }

    let current = new NgbDate(start.year, start.month, start.day);

    while (!current.after(end)) {
      if (this.isDisabled(current)) {
        return true;
      }

      current = this.calendar.getNext(current, 'd', 1);
    }
    return false;
  }

  isInvalidHoveredRange(date: NgbDate): boolean {
    if (!this.disabledDatesSet || this.disabledDatesSet.size === 0 || this.allowDisableDateOverlap) {
      return false;
    }

    if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      return this.includesDisabledDate(this.fromDate, date);
    }

    return false;
  }
}
